// src/Login.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import './Login.css';
import AuthService from './AuthService';
import ForgotPassword from './ForgotPassword';
import EmailVerification from './EmailVerification';
import {
  validateLoginForm,
  validateSignupForm,
  sanitizeInput
} from './ValidationUtils';

export default function Login({ onLogin, initialTab = 'login' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [view, setView] = useState('login'); // 'login', 'forgotPassword', 'emailVerification'
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Update activeTab when initialTab prop changes (for /login vs /signup routes)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingUser, setPendingUser] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userName: '',
    confirmPassword: ''
  });

  // Load saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail
      }));
      setRememberMe(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Sanitize input to prevent XSS
    const sanitizedValue = sanitizeInput(value);

    setFormData({
      ...formData,
      [name]: sanitizedValue
    });

    // Clear errors when user types
    setError('');
    setFieldErrors({
      ...fieldErrors,
      [name]: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      if (activeTab === 'login') {
        // Validate login form
        const validation = validateLoginForm(formData);
        if (!validation.isValid) {
          setFieldErrors(validation.errors);
          setError(t('login.pleaseFixErrors'));
          setLoading(false);
          return;
        }

        const result = await AuthService.login(formData.email, formData.password);
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));

        // Handle "Remember me" functionality
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        onLogin(result.user);
      } else {
        // Validate signup form
        const validation = validateSignupForm(formData);
        if (!validation.isValid) {
          setFieldErrors(validation.errors);
          setError(t('login.pleaseFixErrors'));
          setLoading(false);
          return;
        }

        await AuthService.signup(formData.userName, formData.email, formData.password);

        // Store pending user data
        setPendingUser({
          email: formData.email,
          name: formData.userName
        });

        // Switch to email verification view
        setView('emailVerification');
      }
    } catch (err) {
      if (err.responseData?.errorType === 'UserIsNotConfirmed') {
        setPendingUser({
          email: formData.email,
          name: formData.userName
        });

        // Switch to email verification view
        setView('emailVerification');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setView('forgotPassword');
  };

  const handleEmailVerified = async () => {
    try {
      // In real implementation, complete the signup process
      // For now, simulate login after verification
      const result = await AuthService.login(pendingUser.email, formData.password);
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
      onLogin(result.user);
    } catch (err) {
      setError('Failed to complete signup. Please try again.');
      setView('login');
    }
  };

  const handleBackToLogin = () => {
    setView('login');
    setActiveTab('login');
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    setError('');
    setPendingUser(null);
  };

  const handleViewTerms = (e) => {
    e.preventDefault();
    navigate('/terms');
  };

  const handleViewPrivacyPolicy = (e) => {
    e.preventDefault();
    navigate('/privacy');
  };

  const handleResendVerificationCode = async () => {
    // Mock API call to resend verification code
    await new Promise(resolve => setTimeout(resolve, 1000));
    // In real implementation, call API to resend code
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Exchange Google access token with backend
        const result = await AuthService.googleLogin(tokenResponse.access_token);
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        onLogin(result.user);
      } catch (err) {
        setError(err.message || 'Google sign-in failed. Please try again.');
        setLoading(false);
      }
    },
    onError: () => {
      setError('Google sign-in was cancelled or failed.');
      setLoading(false);
    },
    onNonOAuthError: (err) => {
      console.error('Non-OAuth error:', err);
      setError('Google sign-in failed: ' + err.type);
      setLoading(false);
    },
  });

  // Show Forgot Password view
  if (view === 'forgotPassword') {
    return <ForgotPassword onBack={handleBackToLogin} />;
  }

  // Show Email Verification view
  if (view === 'emailVerification') {
    return (
      <EmailVerification
        email={pendingUser?.email || formData.email}
        onVerified={handleEmailVerified}
        onBack={handleBackToLogin}
        onResendCode={handleResendVerificationCode}
      />
    );
  }

  // Show Login/Signup view
  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="auth-card">
        <a href="#" className="back-button" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t('login.backToHome')}
        </a>

        <div className="auth-header">
          <div className="logo">
            <div className="logo-icon">
              <img
                src="/favicon.ico"
                alt="Logo"
                style={{ width: '30px', height: '30px' }}
              />
            </div>
            <h1>{t('login.title')}</h1>
          </div>
          <p className="auth-subtitle">
            {!showEmailForm
              ? t('login.signInOrCreate')
              : activeTab === 'login'
                ? t('login.welcomeBack')
                : t('login.createAccount')}
          </p>
        </div>

        {!showEmailForm ? (
          <div className="provider-list">
            {error && (
              <div className="error-message">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {error}
              </div>
            )}
            <button type="button" className="provider-btn" onClick={() => setShowEmailForm(true)} disabled={loading}>
              <svg viewBox="0 0 24 24" fill="none" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 6L12 13 2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('login.continueWithEmail')}
            </button>
            <button
              type="button"
              className="provider-btn google-provider-btn"
              onClick={() => { setLoading(true); setError(''); googleLogin(); }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  {t('login.signingInWithGoogle')}
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {t('login.continueWithGoogle')}
                </>
              )}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <a href="#" className="back-to-providers" onClick={(e) => { e.preventDefault(); setShowEmailForm(false); setError(''); setFieldErrors({}); }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('login.allSignInOptions')}
            </a>

            {error && (
              <div className="error-message">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {error}
              </div>
            )}

            {activeTab === 'signup' && (
              <div className="form-group">
                <label htmlFor="userName">{t('login.userName')}</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="userName"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    placeholder="User Name"
                    required
                    className={fieldErrors.userName ? 'input-error' : ''}
                  />
                </div>
                {fieldErrors.userName && (
                  <span className="field-error">{fieldErrors.userName}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">{t('login.emailAddress')}</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  required
                  className={fieldErrors.email ? 'input-error' : ''}
                />
              </div>
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('login.password')}</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                  className={fieldErrors.password ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <span className="field-error">{fieldErrors.password}</span>
              )}
            </div>

            {activeTab === 'signup' && (
              <div className="form-group">
                <label htmlFor="confirmPassword">{t('login.confirmPassword')}</label>
                <div className="input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    required
                    className={fieldErrors.confirmPassword ? 'input-error' : ''}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span className="field-error">{fieldErrors.confirmPassword}</span>
                )}
              </div>
            )}

            {activeTab === 'login' && (
              <div className="form-extras">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>{t('login.rememberMe')}</span>
                </label>
                <a href="#" className="forgot-link" onClick={handleForgotPasswordClick}>
                  {t('login.forgotPassword')}
                </a>
              </div>
            )}

            {activeTab === 'signup' && (
              <label className="checkbox-label terms">
                <input type="checkbox" required />
                <span>{t('login.agreeTerms')} <a href="#" onClick={handleViewTerms}>{t('login.termsOfService')}</a> {t('login.and')} <a href="#" onClick={handleViewPrivacyPolicy}>{t('login.privacyPolicy')}</a></span>
              </label>
            )}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                <>
                  {activeTab === 'login' ? t('login.signIn') : t('login.createAccountBtn')}
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>

            <p className="auth-switch">
              {activeTab === 'login' ? (
                <>{t('login.noAccount')} <a href="#" className="auth-switch-btn" onClick={(e) => { e.preventDefault(); setActiveTab('signup'); setError(''); setFieldErrors({}); }}>{t('login.signUp')}</a></>
              ) : (
                <>{t('login.alreadyHaveAccount')} <a href="#" className="auth-switch-btn" onClick={(e) => { e.preventDefault(); setActiveTab('login'); setError(''); setFieldErrors({}); }}>{t('login.signInLink')}</a></>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}